import { Grid, FormControl, Typography, Slider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { touchpointsMocks } from 'pages/calculator/MediaPlanning/components/Touchpoints/mocks';
import MultiSelectAutocomplete from 'pages/calculator/DigitalFairValue/components/MultiSelectAutocomplete';
import { useParams } from 'react-router-dom';
import convertDecimalToPercentValue from '../../../../Plan/Countries/utils/convertDecimalToPercentValue';
import usePlanStore from '../../../../../../../store/planStore';
import ErrorMessage from '../../../../common/ErrorMessage/ErrorMessage';
import { useAuthContext } from '../../../../../../../context/AuthContext';
import linksConfig from '../../../../../../../config/linking';
import HelperTooltip from '../../../../../../../shared/HelperTooltip/HelperTooltip';

interface PartnerSelectorsProps {
  partners: { id: string; title: string }[];
  partnerBudgetShareConsideration: number;
  fetchedPartnersData?: { partners: string[]; collabPartners: string[] };
  handleChangeStateValues: (
    key: string,
    value: number | string | boolean | { id: string; title: string }
  ) => void;
}

function PartnersSelectors({
  partners,
  partnerBudgetShareConsideration,
  fetchedPartnersData,
  handleChangeStateValues,
}: PartnerSelectorsProps) {
  const { t } = useTranslation();

  const { id } = useParams();
  const { alreadySavedPlanId } = usePlanStore.getState();
  const { userRoles } = useAuthContext();

  const { countryForms, selectedCountry } = usePlanStore();

  const { metaConfig } = countryForms[selectedCountry!];

  const collabPartners = fetchedPartnersData?.collabPartners ?? [];
  const formatPartnerTitle = (partnerName: string) =>
    collabPartners.includes(partnerName)
      ? `${partnerName} (collab)`
      : partnerName;

  const isConsiderationWithoutPartnersError =
    (partnerBudgetShareConsideration ?? 0) > 0 && partners.length === 0;

  const isPartnersError =
    (metaConfig?.error && metaConfig?.partners?.length === 0) ||
    isConsiderationWithoutPartnersError;

  if (metaConfig?.isGoldenRuleMet === false) {
    return null;
  }

  return (
    <Grid container>
      <Grid item xs={3} sx={{ pr: '24px' }}>
        <FormControl fullWidth>
          <MultiSelectAutocomplete
            options={fetchedPartnersData?.partners?.map((partner) => ({
              id: partner,
              title: formatPartnerTitle(partner),
            }))}
            id="partnersMultiSelect"
            label={t('mediaPlanning.partners')}
            noOptionsText={t('mediaPlanning.noPartners')}
            onChange={(_: any, value: string) =>
              handleChangeStateValues('partners', value)
            }
            value={partners.map((partner) => ({
              ...partner,
              title: formatPartnerTitle(partner.id),
            }))}
            limitTags={1}
            disableClearable={false}
            error={isPartnersError}
          />
          {isPartnersError && (
            <ErrorMessage
              message={t(
                isConsiderationWithoutPartnersError
                  ? 'mediaPlanning.errors.partnersRequiredForConsideration'
                  : 'mediaPlanning.errors.partners'
              )}
            />
          )}
        </FormControl>
      </Grid>
      <Grid item xs={3} sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="caption" lineHeight={1}>
            {`${t(
              'mediaPlanning.partnerBudgetShareConsideration'
            )} (${convertDecimalToPercentValue(
              partnerBudgetShareConsideration ?? 0
            )}%)`}
          </Typography>
          <Slider
            valueLabelDisplay="auto"
            value={partnerBudgetShareConsideration}
            step={0.05}
            min={touchpointsMocks.metaSliders.budgetShareConsideration.min}
            max={touchpointsMocks.metaSliders.budgetShareConsideration.max}
            marks={[
              {
                value:
                  touchpointsMocks.metaSliders.budgetShareConsideration.min,
                label: convertDecimalToPercentValue(
                  touchpointsMocks.metaSliders.budgetShareConsideration.min
                ),
              },
              {
                value:
                  touchpointsMocks.metaSliders.budgetShareConsideration.max,
                label: convertDecimalToPercentValue(
                  touchpointsMocks.metaSliders.budgetShareConsideration.max
                ),
              },
            ]}
            valueLabelFormat={(value) =>
              `${convertDecimalToPercentValue(value).toFixed(0)}%`
            }
            onChange={(_, newValue) =>
              handleChangeStateValues(
                'partnerBudgetShareConsideration',
                +newValue
              )
            }
          />
        </Grid>
        <HelperTooltip tooltipData={linksConfig.partnerShareRecommendation} />
      </Grid>
    </Grid>
  );
}

export default PartnersSelectors;
